import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, parseApiError } from '../lib/api';
import { Shield, ExternalLink } from 'lucide-react';

export function ConsentGate() {
  const qc = useQueryClient();
  const [error, setError] = useState('');

  const mut = useMutation({
    mutationFn: api.recordConsent,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-iron-950 p-4">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-forge-900/15 to-transparent" />
      </div>

      <div className="relative z-10 flex w-full max-w-lg flex-col items-center text-center">
        <div className="mb-8 relative">
          <span className="relative flex h-24 w-24 items-center justify-center rounded-full border-2 border-forge-500/40 bg-forge-500/20">
            <Shield className="h-12 w-12 text-forge-400" />
          </span>
        </div>

        <h1 className="mb-3 font-display text-3xl font-black uppercase tracking-tight text-white">
          Privacy & Terms
        </h1>

        <p className="mb-6 max-w-md text-sm leading-relaxed text-iron-400">
          Before you continue, please review and accept our policies. GG Anvil collects your Discord identity, gamertag, match screenshots, and game statistics to run tournaments. Your data is stored securely in the EU and you can request export or deletion at any time.
        </p>

        <div className="mb-8 flex flex-wrap justify-center gap-4">
          <a
            href="https://goodgameanvil.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-sm font-bold text-forge-400 hover:text-forge-300 transition-colors"
          >
            Privacy Policy <ExternalLink className="h-3.5 w-3.5" />
          </a>
          <a
            href="https://goodgameanvil.com/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-sm font-bold text-forge-400 hover:text-forge-300 transition-colors"
          >
            Terms of Service <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>

        <button
          onClick={() => mut.mutate()}
          disabled={mut.isPending}
          className="ember group flex w-full max-w-sm items-center justify-center gap-3 px-8 py-5 font-display text-lg font-black uppercase tracking-wider"
        >
          {mut.isPending ? 'Recording...' : 'I Agree — Continue to Portal'}
        </button>

        {error && (
          <p className="mt-4 text-xs text-red-400">{error}</p>
        )}

        <p className="mt-6 max-w-sm text-[10px] text-iron-500 leading-relaxed">
          By clicking above, you consent to GG Anvil processing your personal information as described in our Privacy Policy, in compliance with the Protection of Personal Information Act (POPIA).
        </p>
      </div>
    </div>
  );
}
